import { ref } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { usePlanningStore } from '@/stores/planning.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { warningCodeEnum } from '@/stores/portal.store'

import { useNotify } from '@/composables/notify'
import { useChips } from '@/composables/chips'

export const useArchive = (options) => {
  const i18n = getI18n()
  const planningStore = usePlanningStore()
  const webSiteStore = useWebSiteStore()

  const { notifySuccess, notifyError } = useNotify()
  const { archivedChip } = useChips()

  const showArchived = ref(false)

  const archiveWarning = ref({
    dialogShow: false,
    warnings: [],
    message: '',
    item: null,
    // blocking = the action cannot proceed (info only), vs. the delete→archive suggestion.
    blocking: false,
  })

  const showArchiveWarningAsync = async(error, item, { blocking = false } = {}) => {
    // Referencing projects may be archived, so resolve against the full set (not {all:true}, which excludes archived).
    const projects = await planningStore.fetchProjectsAsync({ show_archived: true }, false)
    const detail = error.response?.data?.details?.[0]?.errors?.[0]
    archiveWarning.value.warnings = (detail?.params ?? [])
      .map(i => {
        const projectId = +(i.project_id)
        const project = planningStore.isVacationProject(projectId)
          ? planningStore.getVacationProject()
          : projects?.find(p => p.id === projectId)
        return { project }
      })
      .filter(warning => warning.project)
    archiveWarning.value.message = i18n.t(detail?.code)
    archiveWarning.value.blocking = blocking
    archiveWarning.value.item = item
    archiveWarning.value.dialogShow = true
  }

  const showDeleteWarningAsync = async(error, item) => {
    const code = error.response?.data?.details?.[0]?.errors?.[0]?.code
    await showArchiveWarningAsync(error, item, { blocking: code === warningCodeEnum.SERVICE_ARCHIVE_IN_USE })
  }

  const notifySkippedError = (error) => {
    if (error.response?.config?.skipErrorMessage) {
      notifyError(i18n.t(error.response?.data?.details?.[0]?.errors?.[0]?.code || 'error.generic'))
    }
  }

  const archiveDisplayExpr = (data, field = 'name') => {
    if (!data) {
      return ''
    }

    const field_value = typeof field === 'function' ? field(data) : data[field]
    return data.archived ? `${field_value} (${i18n.t('common.archived')})` : field_value
  }

  const archivedStyle = (entity) => {
    if (!entity?.archived) return {}
    return { fontStyle: 'italic', color: 'grey', textDecoration: 'line-through' }
  }

  const archiveCellTemplate = (element, rowData, entity, field) => {
    if (!entity) {
      return
    }

    const field_value = typeof field === 'function' ? field(entity) : entity[field]
    element.textContent = field_value

    if (entity.archived) {
      const style = archivedStyle(entity)
      element.textContent = `${field_value} (${i18n.t('common.archived')})`
      Object.assign(element.style, style)
    }
  }

  const archiveAsync = async(id, archived) => {
    try {
      await options.onArchive(id)
    } catch (error) {
      if (error.response?.status === 409) {
        if (options.onArchiveBlocked) {
          options.onArchiveBlocked({ id }, error)
          return
        }
        await showArchiveWarningAsync(error, { id }, { blocking: true })
        return
      }
      // The interceptor is skipped for service archive, so surface the error here.
      notifySkippedError(error)
      return
    }
    if (archived) {
      notifySuccess(i18n.t('info.restoredSuccessfully'))
    } else {
      notifySuccess(i18n.t('info.archivedSuccessfully'))
    }
    options.gridRef?.value.instance.refresh()
  }

  const onRowRemoving = async(e) => {
    e.cancel = true
    try {
      await options.onDelete(e.data?.id || e.id, { skipErrorMessage: true })
      await options.gridRef?.value.instance.refresh()
    } catch (error) {
      if (error.response?.status === 409) {
        const item = e.data || { id: e.id }
        if (options.onDeleteBlocked) {
          options.onDeleteBlocked(item, error)
          return
        }
        await showDeleteWarningAsync(error, item)
      }
    }
  }

  const selectedIds = ref([])
  let deleteSelectedButton = null

  const deleteSelectedAsync = async(ids, archiveReferenced = false) => {
    try {
      await options.onBulkDelete({ ids, archive_referenced: archiveReferenced }, { skipErrorMessage: true })
    } catch (error) {
      if (error.response?.status === 409) {
        await showDeleteWarningAsync(error, { ids })
      } else {
        notifySkippedError(error)
      }
      return
    }
    notifySuccess(i18n.t('info.deletedSuccessfully'))
    options.gridRef?.value.instance.clearSelection()
    await options.gridRef?.value.instance.refresh()
  }

  const onArchiveWarningConfirm = async() => {
    const { item } = archiveWarning.value
    if (item.ids) await deleteSelectedAsync(item.ids, true)
    else await archiveAsync(item.id, false)
  }

  const onDeleteSelectionChanged = (e) => {
    selectedIds.value = e.selectedRowsData.map(row => row.id)
    deleteSelectedButton?.option('disabled', selectedIds.value.length === 0)
  }

  const deleteSelectedToolbarItem = {
    widget: 'dxButton',
    location: 'after',
    visible: webSiteStore.can(webSiteStore.permissionFlagEnum.DELETE),
    options: {
      icon: 'trash',
      hint: i18n.t('common.deleteSelected'),
      disabled: true,
      onClick: () => deleteSelectedAsync(selectedIds.value),
      onInitialized: (e) => { deleteSelectedButton = e.component },
    },
  }

  const onRowPrepared = (e) => {
    if (e.rowType === 'data' && e.data?.archived_at) {
      e.rowElement.classList.add('dx-disabled-row')
    }
  }

  const archiveButton = {
    text: i18n.t('common.archive'),
    template: 'archive-button',
    visible: (data) => data && !data.archived && webSiteStore.can(webSiteStore.permissionFlagEnum.DELETE),
    onClick: (e) => archiveAsync(e.row?.data?.id, e.row?.data?.archived),
  }

  return {
    archiveAsync,
    archiveButton,
    archiveCellTemplate,
    archivedChip,
    archiveDisplayExpr,
    archivedStyle,
    archiveWarning,
    deleteSelectedToolbarItem,
    onArchiveWarningConfirm,
    onDeleteSelectionChanged,
    onRowPrepared,
    onRowRemoving,
    showArchived,
  }
}
